import React, { useState, useEffect, useCallback } from 'react';
import TabBar from '../components/TabBar';
import TutorialButton from '../components/TutorialButton';
import BizSwitch from '../components/BizSwitch';
import PuzzleMaterialView from '../components/PuzzleMaterialView';
import { useBizMode } from '../utils/bizMode';
import { useToast } from '../components/Toast';
import { QrCode, CheckCircle2, RefreshCw, Download, Image } from 'lucide-react';
import api from '../utils/api';
import { useSocket } from '../contexts/SocketContext';
import { saveImageToAlbum } from '../utils/saveImage';
import { timeAgo, formatBJ } from '../utils/datetime';

// ── 通用多图卡片（问候图 / 回访图）──────────────────────────────────────────
// title：图片名称，用于保存按钮、“尚未设置”提示；heading：卡片顶部显示的标题（不传就用 title）
function MultiImageCard({ title, heading, images, onSave, savingNo, emptyIcon }) {
  const [currentNo, setCurrentNo] = useState(null);

  useEffect(() => {
    if (images.length === 0) { setCurrentNo(null); return; }
    if (!images.some(img => img.img_no === currentNo)) setCurrentNo(images[0].img_no);
  }, [images, currentNo]);

  const current = images.find(img => img.img_no === currentNo) || null;

  if (images.length === 0) {
    return (
      <div className="card empty-state" style={{ marginBottom: 12 }}>
        <div className="empty-icon">{emptyIcon}</div>
        <p>管理员尚未设置{title}</p>
      </div>
    );
  }

  return (
    <div className="card" style={{ textAlign: 'center', padding: 24, marginBottom: 12 }}>
      <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 14, textAlign: 'left' }}>{heading || title}</div>

      {images.length > 1 && (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
          {images.map(img => (
            <button
              key={img.img_no}
              onClick={() => setCurrentNo(img.img_no)}
              style={{
                padding: '6px 16px', borderRadius: 20, fontSize: 13, fontWeight: 600,
                border: `1.5px solid ${currentNo === img.img_no ? 'var(--c-primary)' : 'var(--c-border)'}`,
                background: currentNo === img.img_no ? 'var(--c-primary)' : 'var(--c-bg)',
                color: currentNo === img.img_no ? '#fff' : 'var(--c-text-secondary)',
                cursor: 'pointer',
              }}
            >
              图{img.img_no}
            </button>
          ))}
        </div>
      )}

      {current ? (
        <>
          <img
            src={current.image_data}
            alt={`${title}${current.img_no}`}
            style={{ display: 'block', margin: '0 auto', maxWidth: '100%', maxHeight: 320, borderRadius: 12 }}
            onError={e => { e.target.style.display = 'none'; }}
          />
          <button
            onClick={() => onSave(current)}
            disabled={savingNo === current.img_no}
            className="btn btn-primary"
            style={{ marginTop: 16, width: '100%', height: 46, fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
          >
            <Download size={16} />
            {savingNo === current.img_no ? '保存中...' : `保存${title}`}
          </button>
        </>
      ) : (
        <p style={{ padding: '30px 0', color: 'var(--c-text-secondary)' }}>加载中，请稍候…</p>
      )}
    </div>
  );
}

// ── 主页面 ────────────────────────────────────────────────────────────────────
export default function QRCodePage() {
  // 用户端只拿到"当前分配给自己的那张码"，不拿全部列表
  const [assignedCode, setAssignedCode] = useState(null);  // { code_no, image_data, created_at }
  const [loading,      setLoading]      = useState(true);
  const [updating,     setUpdating]     = useState(false);
  const [greetings,    setGreetings]    = useState([]);
  const [revisits,     setRevisits]     = useState([]);
  const [savingGreeting, setSavingGreeting] = useState(null);
  const [savingRevisit,  setSavingRevisit]  = useState(null);
  const { subscribe } = useSocket();
  const { showToast } = useToast();
  const [bizMode] = useBizMode(); // 排五 / 证券

  // 下次切换时间：每3小时（北京时间 0/3/6/9/12/15/18/21 整点）

  const fetchAssignedCode = useCallback(() => {
    return api.get('/admin/qrcode-for-user').then(r => {
      setAssignedCode(r.data.code || null);
      setUpdating(false);
    });
  }, []);

  const fetchAll = useCallback(() => {
    Promise.all([
      fetchAssignedCode(),
      api.get('/admin/greeting-images'),
      api.get('/admin/revisit-images'),
    ]).then(([, greetRes, revisitRes]) => {
      setGreetings(greetRes.data.images || []);
      setRevisits(revisitRes.data.images || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [fetchAssignedCode]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Socket 实时推送
  useEffect(() => {
    if (!subscribe) return;
    return subscribe((event) => {
      if (event === 'qrcode_updated') {
        setUpdating(true);
        setTimeout(() => fetchAssignedCode(), 300);
      }
      if (event === 'greeting_images_updated') {
        api.get('/admin/greeting-images').then(r => setGreetings(r.data.images || []));
      }
      if (event === 'revisit_images_updated') {
        api.get('/admin/revisit-images').then(r => setRevisits(r.data.images || []));
      }
    });
  }, [subscribe, fetchAssignedCode]);

  const handleSaveQR = async () => {
    if (!assignedCode) return;
    try {
      await saveImageToAlbum(assignedCode.image_data, `qrcode${assignedCode.code_no || ''}.png`);
      showToast('已打开保存面板', 'success');
    } catch {
      showToast('保存失败，请长按二维码图片保存', 'error');
    }
  };

  const handleSaveGreeting = async (img) => {
    setSavingGreeting(img.img_no);
    try {
      await saveImageToAlbum(img.image_data, `greeting${img.img_no}.png`);
      showToast('已打开保存面板', 'success');
    } catch {
      showToast('保存失败，请长按图片保存', 'error');
    } finally { setSavingGreeting(null); }
  };

  const handleSaveRevisit = async (img) => {
    setSavingRevisit(img.img_no);
    try {
      await saveImageToAlbum(img.image_data, `revisit${img.img_no}.png`);
      showToast('已打开保存面板', 'success');
    } catch {
      showToast('保存失败，请长按图片保存', 'error');
    } finally { setSavingRevisit(null); }
  };

  return (
    <div className="page">
      <div className="header" style={{ display: 'flex', alignItems: 'center' }}>
        <span style={{ flex: 1 }}>获客素材</span>
        <TutorialButton />
      </div>

      {/* 排五 / 证券 业务切换 */}
      <div style={{ padding: '10px 16px 0' }}><BizSwitch /></div>

      <div style={{ padding: '12px 16px' }}>
        {bizMode === 'puzzle' ? (
          <PuzzleMaterialView />
        ) : loading ? (
          <p style={{ textAlign: 'center', padding: 40, color: 'var(--c-text-secondary)' }}>加载中...</p>
        ) : (
          <>
            {/* ── 问候图卡片 ── */}
            <MultiImageCard
              title="问候图"
              heading="第 1 步"
              images={greetings}
              onSave={handleSaveGreeting}
              savingNo={savingGreeting}
              emptyIcon={<Image size={40} color="var(--c-text-secondary)" strokeWidth={1.2} />}
            />

            {/* ── 二维码卡片 ── */}
            {!assignedCode ? (
              <div className="card empty-state" style={{ marginBottom: 12 }}>
                <div className="empty-icon"><QrCode size={40} color="var(--c-text-secondary)" strokeWidth={1.2} /></div>
                <p>管理员尚未设置二维码</p>
              </div>
            ) : (
              <div className="card" style={{ textAlign: 'center', padding: 24, marginBottom: 12 }}>

                {/* ── 卡片顶部 ── */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 16 }}>
                  <QrCode size={15} color="var(--c-primary)" />
                  <span style={{ fontSize: 15, fontWeight: 700 }}>第 2 步</span>
                </div>

                {/* 二维码图片 */}
                <img
                  src={assignedCode.image_data}
                  alt={`二维码${assignedCode.code_no}`}
                  style={{ display: 'block', margin: '0 auto', maxWidth: '100%', maxHeight: 300, borderRadius: 12 }}
                  onError={e => { e.target.style.display = 'none'; e.target.dataset.failed = '1'; }}
                />

                {/* 状态标签 */}
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: 14, marginBottom: 8 }}>
                  {updating ? (
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 12px',
                      background: 'rgba(255,149,0,0.1)', color: '#b07000', borderRadius: 20, fontSize: 13, fontWeight: 600,
                    }}>
                      <RefreshCw size={13} /> 正在更新...
                    </span>
                  ) : (
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 12px',
                      background: 'rgba(52,199,89,0.1)', color: 'var(--c-success)', borderRadius: 20, fontSize: 13, fontWeight: 600,
                    }}>
                      <CheckCircle2 size={13} /> 当前为最新
                    </span>
                  )}
                </div>

                {/* 更新时间 */}
                {assignedCode.created_at && (
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 13, color: 'var(--c-text-secondary)' }}>{timeAgo(assignedCode.created_at)}</div>
                    <div style={{ fontSize: 12, color: 'var(--c-text-tertiary)', marginTop: 2 }}>{formatBJ(assignedCode.created_at)}</div>
                  </div>
                )}

                {/* 保存按钮 */}
                <button
                  onClick={handleSaveQR}
                  className="btn btn-primary"
                  style={{ marginTop: 14, width: '100%', height: 46, fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                >
                  <Download size={16} /> 保存二维码
                </button>
              </div>
            )}

            {/* ── 回访图卡片 ── */}
            <MultiImageCard
              title="回访图"
              heading="第 3 步(30分钟)"
              images={revisits}
              onSave={handleSaveRevisit}
              savingNo={savingRevisit}
              emptyIcon={<Image size={40} color="var(--c-text-secondary)" strokeWidth={1.2} />}
            />
          </>
        )}
      </div>

      <TabBar mode="user" />
    </div>
  );
}
